import type { definitions } from '@luxuryescapes/contract-svc-promo';

import type { PromoLog, PromoTypesAndUnknown } from '~/services/PromoService';

type PromoLogRowV2 = definitions['Promo log row'];

function coercePromoLogDetails(raw: PromoLog['details'] | unknown | null | undefined): PromoLog['details'] {
  if (raw && typeof raw === 'object' && Array.isArray((raw as PromoLog['details']).discountItems)) {
    return raw as PromoLog['details'];
  }
  return {
    logResults: {
      inputOrder: { region: '', brand: '', items: [] },
      logAttemptBy: '',
      promoToggles: [],
      clientSideAmount: 0,
      serverSideAmount: 0,
      promoLogThresholdDifference: 0,
    },
    discountItems: [],
  };
}

/**
 * V2 `logs` rows match {@link PromoLogRowV2} in the contract; runtime payloads may still omit legacy FKs.
 * Optional fields here document what we normalize when building {@link PromoLog}.
 */
type PromoLogV2RowInput = Partial<
  Pick<
    PromoLogRowV2,
    'id_promo_code' | 'fk_orders' | 'used_by' | 'parent_id_promo_code' | 'email' | 'details' | 'deleted'
  >
> &
  Pick<PromoLogRowV2, 'code_name' | 'promo_type' | 'brand' | 'created_at' | 'amount' | 'currency'>;

function normalizePromoLogV2RowToPromoLog(
  r: PromoLogV2RowInput,
  promoCodeName: string,
  promoType: PromoTypesAndUnknown,
): PromoLog {
  return {
    id_promo_code: r.id_promo_code ?? '',
    code_name: r.code_name,
    promo_type: promoType,
    brand: r.brand,
    created_at: r.created_at,
    fk_orders: r.fk_orders ?? '',
    amount: r.amount,
    currency: r.currency,
    used_by: r.used_by ?? '',
    email: r.email ?? '',
    details: coercePromoLogDetails(r.details),
    parent_id_promo_code: r.parent_id_promo_code ?? null,
    deleted: r.deleted ? new Date(r.deleted) : null,
    promo_code_name: promoCodeName,
  };
}

type PromoLogsContext = {
  code_name: string;
  promo_type: PromoTypesAndUnknown;
};

/**
 * Maps `getLogsV2` `result.logs` into the shape expected by the promo logs DataGrid (`PromoLog`).
 */
export function mapPromoLogV2RowsToPromoLogs(
  logs: Array<PromoLogRowV2> | undefined | null,
  promo: PromoLogsContext,
): Array<PromoLog> {
  if (!logs?.length) {
    return [];
  }
  return logs.map((row) => normalizePromoLogV2RowToPromoLog(row, promo.code_name, promo.promo_type));
}
